import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import EntityCardList from '../controls/EntityCardList';
import CurrencyField from '../fields/CurrencyField';
import NavRow from '../fields/NavRow';
import SelectField from '../fields/SelectField';
import { TaskDefinition, TaskStepRenderArgs } from '../QuestionnaireShell';
import { AreasOfLawValues, FixedRatesValues, RateExceptionRow } from '../types';
import { currencyAmountSchema, zodResolver } from '../validation';

const rowSchema = z.object({
  radio1: z.literal('Exception'),
  areaOfLaw: z.string().min(1, 'Area of Law is required'),
  amount: currencyAmountSchema,
});

const schema = z.object({
  fixedRates: z.array(rowSchema),
});

const DEFAULTS: FixedRatesValues = { fixedRates: [] };

const makeEmptyRow = (): RateExceptionRow => ({
  radio1: 'Exception',
  areaOfLaw: '',
  amount: { display: '', value: 0 },
});

const FixedRatesForm = ({
  initialValues,
  allValues,
  onComplete,
  onBack,
  onCancelEdit,
  submitLabel,
}: TaskStepRenderArgs) => {
  const defaults = (initialValues as FixedRatesValues | undefined) ?? DEFAULTS;
  const {
    control,
    handleSubmit,
    trigger,
    formState: { isValid },
  } = useForm<FixedRatesValues>({
    defaultValues: defaults,
    resolver: zodResolver(schema),
    mode: 'onChange',
  });

  const areasOptions = useMemo(() => {
    const selectedAreas = ((allValues.task6 as AreasOfLawValues | undefined)?.areasOfLaw ?? []).flatMap(
      (cat) => cat.choices,
    );
    return selectedAreas.map((title) => ({ title, value: title }));
  }, [allValues.task6]);

  const onSubmit = handleSubmit((values) => onComplete(values));

  return (
    <form className="rnq-form" onSubmit={(e) => e.preventDefault()} style={{ marginTop: '1rem' }}>
      <EntityCardList<FixedRatesValues, RateExceptionRow>
        control={control}
        name="fixedRates"
        entityLabel="Fixed Rate"
        trigger={trigger}
        makeEmptyRow={makeEmptyRow}
        isRowValid={(row) => rowSchema.safeParse(row).success}
        renderers={{
          getPrimary: (row) => row.amount?.display || '',
          getSecondary: (row) => row.areaOfLaw || null,
          secondaryIcon: 'id_gavel',
        }}
        renderEditForm={(index) => (
          <>
            <SelectField
              control={control}
              name={`fixedRates.${index}.areaOfLaw`}
              label="Area of Law"
              required
              options={areasOptions}
            />
            <CurrencyField control={control} name={`fixedRates.${index}.amount`} label="Fixed Rate (USD)" required />
          </>
        )}
      />
      <NavRow
        onBack={onBack}
        onCancelEdit={onCancelEdit}
        onSubmit={onSubmit}
        submitLabel={submitLabel}
        disabled={!isValid}
      />
    </form>
  );
};

const FixedRates: TaskDefinition = {
  key: 'task10',
  label: 'Fixed Rates',
  title: 'Are there any areas of law where you provide service for a fixed rate?',
  render: (args) => <FixedRatesForm {...args} />,
};

export default FixedRates;
